import { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { LogOut, User2, Loader2 } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Progress } from "@/components/ui/Progress";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import {
  useOnboardingStore,
  PROGRESS_SEGMENTS,
  STEP_SLUGS,
  segmentForStep,
  stepFromSlug,
  type OnboardingStep,
  type ProgressSegmentKey,
} from "@/stores/onboardingStore";
import { onboardingApi, type OnboardingMeResponse, type DnsStatusResponse } from "@/lib/api";
import {
  OnboardingTimeline,
  itemsFromDnsStatus,
  type TimelineItem,
} from "./components/OnboardingTimeline";
import { StepRegister } from "./steps/StepRegister";
import { StepVerifyWaiting } from "./steps/StepVerifyWaiting";
import { StepVerifyToken } from "./steps/StepVerifyToken";
import { StepLogin } from "./steps/StepLogin";
import { StepDomainBranch } from "./steps/StepDomainBranch";
import { StepCompanyForm } from "./steps/StepCompanyForm";
import { StepTeammateRequested } from "./steps/StepTeammateRequested";
import { StepSubdomain } from "./steps/StepSubdomain";
import { StepComplete } from "./steps/StepComplete";

// Onboarding wizard. Anonymous up to and including email verification, then
// auth-gated through sign-in. The right-hand drawer shows live stage status
// for authenticated users.
//
// Routes:
//   /onboarding/verify  → StepVerifyToken (anonymous, token capability)
//   /onboarding/login   → StepLogin       (anonymous → authenticated)
//   /onboarding         → StepSwitcher    (auth-gated for any post-verify step)
export function OnboardingShell() {
  const step = useOnboardingStore((s) => s.step);
  const currentSegment = segmentForStep(step);
  const currentIndex = PROGRESS_SEGMENTS.findIndex((s) => s.key === currentSegment);
  const pct = ((currentIndex + 1) / PROGRESS_SEGMENTS.length) * 100;
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-brand-midnight text-brand-ink flex flex-col">
      <header className="sticky top-0 z-10 border-b border-border/60 bg-brand-midnight/95 backdrop-blur">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <Logo />
            <div className="flex items-center gap-4">
              <span
                className="font-mono text-xs text-muted-foreground hidden sm:inline"
                aria-live="polite"
              >
                Step {currentIndex + 1} of {PROGRESS_SEGMENTS.length}
              </span>
              {user ? <UserMenu /> : null}
            </div>
          </div>
          <Progress
            value={pct}
            className="mt-3"
            aria-label={`Step ${currentIndex + 1} of ${PROGRESS_SEGMENTS.length}`}
          />
          <StepBreadcrumbs current={currentSegment} />
        </div>
      </header>

      <Routes>
        <Route
          path="/onboarding/verify"
          element={
            <Layout>
              <StepVerifyToken />
            </Layout>
          }
        />
        <Route
          path="/onboarding/login"
          element={
            <Layout>
              <StepLogin />
            </Layout>
          }
        />
        <Route
          path="/onboarding"
          element={
            <Layout withDrawer>
              <AuthGatedSwitcher />
            </Layout>
          }
        />
        <Route path="*" element={<Navigate to="/onboarding" replace />} />
      </Routes>
    </div>
  );
}

function Layout({
  children,
  withDrawer,
}: {
  children: React.ReactNode;
  withDrawer?: boolean;
}) {
  const { user } = useAuth();
  const showDrawer = withDrawer && !!user;
  return (
    <main className="flex-1 flex items-start justify-center p-6 pt-10">
      <div
        className={
          showDrawer
            ? "w-full max-w-[1040px] grid grid-cols-1 lg:grid-cols-[minmax(0,640px)_320px] gap-8"
            : "w-full max-w-[640px]"
        }
      >
        <div className="min-w-0">{children}</div>
        {showDrawer ? <StatusDrawer /> : null}
      </div>
    </main>
  );
}

// AuthGatedSwitcher hydrates from /me on first mount (so a fresh sign-in or
// cross-device flow lands on the correct step) and bounces unauthenticated
// users to the login step.
function AuthGatedSwitcher() {
  const navigate = useNavigate();
  const loc = useLocation();
  const { user, idToken, loading: authLoading } = useAuth();
  const step = useOnboardingStore((s) => s.step);
  const setStep = useOnboardingStore((s) => s.setStep);
  const setOnboarding = useOnboardingStore((s) => s.setOnboarding);
  const setVerified = useOnboardingStore((s) => s.setVerified);
  const setCompany = useOnboardingStore((s) => s.setCompany);
  const setSubdomainInStore = useOnboardingStore((s) => s.setSubdomain);
  const reset = useOnboardingStore((s) => s.reset);

  const [hydrating, setHydrating] = useState(true);
  // Track the last user.uid we successfully hydrated for. This breaks the
  // re-render loop where StepDomainBranch's local refinement
  // (setStep("company-form")) re-triggered our `step` dep, which re-ran /me,
  // which set step BACK to the backend-derived value ("domain-branch"),
  // which made StepDomainBranch refine again — ping-pong at ~43 mounts/sec
  // that swallowed every keystroke on the company form. /me only needs to
  // run once per (user, idToken) pair, not on every step change.
  const hydratedUid = useRef<string | null>(null);

  // ?step= URL → store on first mount. Lets the user bookmark a wizard step
  // (e.g. /onboarding?step=profile) and land back on it; also makes browser
  // back/forward navigate between wizard steps. Only seeds the store when
  // the slug is recognized AND we have authoritative state to back it up
  // (i.e. /me has hydrated — otherwise the user would jump to a step they
  // can't complete).
  const slugApplied = useRef(false);
  useEffect(() => {
    if (slugApplied.current) return;
    if (hydratedUid.current === null) return; // wait for /me
    const slug = new URLSearchParams(loc.search).get("step");
    const target = stepFromSlug(slug);
    if (target && target !== step) {
      setStep(target);
    }
    slugApplied.current = true;
  }, [loc.search, step, setStep]);

  // store → ?step= URL on every step change. Push (not replace) when the
  // step transitions normally so the browser back button walks the user
  // back through wizard steps. Replace on the very first sync to avoid
  // littering history with the bootstrap render.
  const lastSyncedSlug = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const slug = STEP_SLUGS[step];
    if (slug === undefined) return;
    const search = new URLSearchParams(loc.search);
    const current = search.get("step");
    if (slug === null) {
      if (!current) return;
      search.delete("step");
    } else {
      if (current === slug) return;
      search.set("step", slug);
    }
    const next = `${loc.pathname}${search.toString() ? "?" + search.toString() : ""}`;
    const isFirst = lastSyncedSlug.current === undefined;
    navigate(next, { replace: isFirst });
    lastSyncedSlug.current = slug;
  }, [step, loc.pathname, loc.search, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      hydratedUid.current = null;
      // Pre-auth steps that don't need /me hydration.
      if (step === "register" || step === "verify-email") {
        setHydrating(false);
        return;
      }
      // Auth was lost mid-flow (Firebase token expired, user deleted server
      // side, etc.). The persisted wizard step would otherwise re-hydrate the
      // user back into a step they can't complete — wipe it and route to the
      // login page cleanly instead.
      reset();
      navigate("/onboarding/login", { replace: true, state: { from: loc.pathname } });
      return;
    }
    if (!idToken) {
      setHydrating(false);
      return;
    }
    // If we've already hydrated for this user, do NOT re-fetch /me when
    // local-only step changes ripple through. Without this guard, the wizard
    // local refinement (StepDomainBranch -> "company-form") and the backend
    // hydration (/me -> "domain-branch") fight forever.
    if (hydratedUid.current === user.uid) {
      setHydrating(false);
      return;
    }
    hydratedUid.current = user.uid;
    let cancelled = false;
    (async () => {
      try {
        const me = await onboardingApi.me(idToken);
        if (cancelled) return;
        setOnboarding(me.onboarding_id, me.email);
        setVerified(me.domain, false /* re-derive on next backend touch */);
        if (me.client_id && me.client_code) {
          setCompany(me.client_id, me.client_code);
        }
        if (me.subdomain) {
          setSubdomainInStore(me.subdomain);
        }
        setStep(stageToStep(me));
      } catch (err) {
        // 404 here means there's no onboarding row for this Firebase user —
        // they probably signed in for the wrong account or the row was reset.
        // Surface gracefully by sending them to register.
        console.error("onboarding /me", err);
      } finally {
        if (!cancelled) setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, idToken, authLoading, navigate, loc.pathname, step, setOnboarding, setVerified, setCompany, setSubdomainInStore, setStep, reset]);

  if (hydrating || authLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-brand-violet" />
      </div>
    );
  }

  return renderStep(step);
}

function stageToStep(me: OnboardingMeResponse): OnboardingStep {
  switch (me.stage) {
    case "registered":
      return "verify-email";
    case "email_verified":
      return "domain-branch";
    case "company_profile":
      return "subdomain";
    case "subdomain_selected":
    case "dns_pending":
    case "dns_verified":
    case "subdomain_ready":
    case "first_agent_hired":
    case "completed":
      return "complete";
    default:
      return "domain-branch";
  }
}

function renderStep(step: OnboardingStep) {
  switch (step) {
    case "register":
      return <StepRegister />;
    case "verify-email":
      return <StepVerifyWaiting />;
    case "login":
      return <StepLogin />;
    case "domain-branch":
      return <StepDomainBranch />;
    case "company-form":
      return <StepCompanyForm />;
    case "teammate-requested":
      return <StepTeammateRequested />;
    case "subdomain":
      return <StepSubdomain />;
    case "complete":
      return <StepComplete />;
    default:
      return <StepRegister />;
  }
}

function StepBreadcrumbs({ current }: { current: ProgressSegmentKey }) {
  const idx = PROGRESS_SEGMENTS.findIndex((s) => s.key === current);
  return (
    <ol className="mt-3 hidden md:flex items-center gap-2 overflow-x-auto text-[11px] text-muted-foreground">
      {PROGRESS_SEGMENTS.map((seg, i) => (
        <li key={seg.key} className="flex items-center gap-2">
          <span
            className={
              i === idx
                ? "text-brand-violet font-medium"
                : i < idx
                ? "text-foreground"
                : "text-muted-foreground"
            }
          >
            {seg.label}
          </span>
          {i < PROGRESS_SEGMENTS.length - 1 && <span className="opacity-40">/</span>}
        </li>
      ))}
    </ol>
  );
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const reset = useOnboardingStore((s) => s.reset);

  const handleSignOut = async () => {
    try {
      await signOut();
    } finally {
      reset();
      navigate("/onboarding/login", { replace: true });
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="hidden sm:inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-violet/15 text-brand-violet">
        <User2 className="h-4 w-4" />
      </span>
      <span className="hidden md:inline text-xs text-muted-foreground truncate max-w-[180px]">
        {user?.email}
      </span>
      <Button variant="ghost" size="sm" onClick={handleSignOut}>
        <LogOut className="h-4 w-4" />
        Sign out
      </Button>
    </div>
  );
}

// Right-hand drawer summarizing the user's onboarding progress as a timeline.
// Pre-subdomain steps (verify / signin / company / subdomain) are derived
// from the wizard's local step. Once SetSubdomain has run, the drawer also
// polls /dns-status and appends the provisioning-timeline rows so the user
// sees CNAME → resolve → HTTPS → email → ready unfold live.
function StatusDrawer() {
  const { idToken } = useAuth();
  const step = useOnboardingStore((s) => s.step);
  const subdomain = useOnboardingStore((s) => s.subdomain);
  const clientCode = useOnboardingStore((s) => s.clientCode);
  const onboardingId = useOnboardingStore((s) => s.onboardingId);

  const [dnsStatus, setDnsStatus] = useState<DnsStatusResponse | null>(null);

  // Poll /dns-status while the user is on the wizard terminal. Cadence
  // matches StepComplete (30s with explicit re-fetch on demand) so the two
  // panels never disagree on which milestone the row currently shows.
  useEffect(() => {
    if (!idToken || !onboardingId) return;
    if (step !== "complete") return;
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await onboardingApi.getDnsStatus(onboardingId, idToken);
        if (cancelled) return;
        setDnsStatus(res);
      } catch {
        /* StepComplete surfaces network errors; we stay silent in the drawer. */
      }
    };
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [idToken, onboardingId, step]);

  const currentSeg = segmentForStep(step);
  const currentIdx = PROGRESS_SEGMENTS.findIndex((s) => s.key === currentSeg);

  // Wizard-step rows (intrinsic to the local store) — done/active flags
  // computed from the breadcrumb position. We DROP the "Subdomain chosen"
  // row once we reach the complete step because the provisioning timeline's
  // "Subdomain reserved" row covers the same fact (timestamp-backed). Two
  // rows for the same milestone was the left/right panel discrepancy users
  // were seeing.
  const wizardRows: TimelineItem[] = [
    { key: "verify", label: "Email verified", done: PROGRESS_SEGMENTS.findIndex(s => s.key === "verify") < currentIdx },
    { key: "signin", label: "Signed in", done: PROGRESS_SEGMENTS.findIndex(s => s.key === "signin") < currentIdx },
    {
      key: "company",
      label: "Company profile saved",
      detail: clientCode ?? undefined,
      done: PROGRESS_SEGMENTS.findIndex(s => s.key === "company") < currentIdx,
    },
  ];
  if (step !== "complete") {
    wizardRows.push({
      key: "subdomain",
      label: "Subdomain chosen",
      detail: subdomain ? `${subdomain}.virtualdesk.ai` : undefined,
      done: PROGRESS_SEGMENTS.findIndex(s => s.key === "subdomain") < currentIdx,
    });
  }

  // Provisioning rows — only render once we're at the complete step (i.e.,
  // SetSubdomain has fired). Avoids confusing pending rows during earlier
  // wizard steps. Same itemsFromDnsStatus helper StepComplete uses, so
  // both panels are guaranteed to render identical row state.
  const provisioningRows: TimelineItem[] =
    step === "complete" ? itemsFromDnsStatus(dnsStatus, subdomain) : [];

  return (
    <aside
      aria-label="Onboarding status"
      className="rounded-lg border border-border bg-surface-1 p-5 self-start lg:sticky lg:top-[148px]"
    >
      <h2 className="font-display text-lg mb-1">Your onboarding</h2>
      <p className="text-xs text-muted-foreground mb-4">
        Each step is logged with a timestamp. Close the tab anytime — we'll
        email you when your workspace is ready.
      </p>
      <OnboardingTimeline items={[...wizardRows, ...provisioningRows]} />
    </aside>
  );
}
